"use client";

import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const SESSION_DISMISS_KEY = "granbay.installPrompt.sessionDismissed";
const PERMA_DISMISS_KEY = "granbay.installPrompt.dismissedAt";
const PERMA_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

function isWithinCooldown(): boolean {
  try {
    const raw = localStorage.getItem(PERMA_DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number.parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < PERMA_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function isAlreadyInstalled(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  const navStandalone = (window.navigator as { standalone?: boolean })
    .standalone;
  return navStandalone === true;
}

export function InstallPromptBanner() {
  const { t } = useTranslation();
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled()) return;
    if (isWithinCooldown()) return;
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return;
    } catch {
      // sessionStorage may be unavailable (privacy mode) — proceed anyway.
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setEvt(null);
      setHidden(true);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden || !evt) return null;

  async function onInstall() {
    if (!evt) return;
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      window.dispatchEvent(
        new CustomEvent("granbay.install_prompt_choice", {
          detail: { outcome: choice.outcome, platform: choice.platform },
        }),
      );
    } catch {
      // User-agent rejected the call (already-shown, gesture-required) —
      // hide the banner so we don't keep nagging.
    }
    setEvt(null);
    setHidden(true);
  }

  function dismissSession() {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      // best-effort
    }
    setHidden(true);
  }

  function dismissPermanent() {
    try {
      localStorage.setItem(PERMA_DISMISS_KEY, String(Date.now()));
    } catch {
      // best-effort
    }
    setHidden(true);
  }

  return (
    <div
      role="region"
      aria-label={t("installPrompt.aria")}
      className="w-full border-b"
      style={{
        borderColor:
          "color-mix(in srgb, var(--color-status-info) 30%, transparent)",
        background:
          "color-mix(in srgb, var(--color-status-info) 12%, transparent)",
        color: "var(--color-status-info)",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2 text-[12px]">
        <Download size={14} strokeWidth={2} aria-hidden="true" />
        <div className="flex-1 leading-tight">
          <strong className="mr-1">{t("installPrompt.title")}</strong>
          <span className="opacity-80">{t("installPrompt.body")}</span>
        </div>
        <button
          type="button"
          onClick={onInstall}
          className="rounded-md px-3 py-1 text-[11px] font-bold uppercase tracking-[0.5px]"
          style={{
            background: "var(--color-status-info)",
            color: "white",
          }}
        >
          {t("installPrompt.cta")}
        </button>
        <button
          type="button"
          onClick={dismissPermanent}
          className="text-[11px] underline opacity-70 hover:opacity-100"
          aria-label={t("installPrompt.notNowAria")}
        >
          {t("installPrompt.notNow")}
        </button>
        <button
          type="button"
          onClick={dismissSession}
          className="opacity-70 hover:opacity-100"
          aria-label={t("installPrompt.closeAria")}
        >
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
